import React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Paper } from '@material-ui/core';
import SchoolIcon from '@material-ui/icons/School';
import GroupIcon from '@material-ui/icons/Group';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { getThemeColors } from '../utils/themeUtils';

const useStyles = makeStyles(theme => ({
	root: {
		marginBottom: theme.spacing(3),
		width: '100%'
	},
	sectionTitleContainer: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		marginBottom: theme.spacing(2),
		marginTop: '15px'
	},
	sectionDivider: {
		flex: 1,
		height: '1px',
		backgroundColor: '#334155'
	},
	sectionTitle: {
		fontSize: '12px',
		fontWeight: 600,
		color: '#64748b',
		padding: `0 ${theme.spacing(2)}px`,
		whiteSpace: 'nowrap',
		textTransform: 'uppercase',
		letterSpacing: '0.5px'
	},
	scrollContainer: {
		display: 'flex',
		gap: theme.spacing(2),
		overflowX: 'auto',
		overflowY: 'hidden',
		paddingBottom: theme.spacing(1),
		'&::-webkit-scrollbar': {
			display: 'none'
		},
		msOverflowStyle: 'none',
		scrollbarWidth: 'none'
	},
	workshopCard: {
		minWidth: '320px',
		maxWidth: '320px',
		minHeight: '45px',
		backgroundColor: '#1e293b',
		border: '1px solid #334155',
		borderRadius: '8px',
		padding: theme.spacing(1.5),
		paddingBottom: theme.spacing(5),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
		position: 'relative',
		transition: 'all 0.2s ease',
		cursor: 'pointer',
		'&:hover': {
			borderColor: '#475569',
			transform: 'translateY(-2px)',
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
		}
	},
	workshopHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1)
	},
	iconContainer: {
		width: '32px',
		height: '32px',
		borderRadius: '6px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0
	},
	activeIcon: {
		backgroundColor: '#22c55e',
		'& svg': {
			color: '#ffffff'
		}
	},
	inactiveIcon: {
		backgroundColor: '#475569',
		'& svg': {
			color: '#94a3b8'
		}
	},
	workshopInfo: {
		flex: 1,
		minWidth: 0
	},
	workshopTitle: {
		fontSize: '13px',
		fontWeight: 600,
		color: '#f8fafc',
		marginBottom: theme.spacing(0.3),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		whiteSpace: 'normal',
		lineHeight: 1.3,
		maxHeight: '34px'
	},
	workshopCode: {
		fontSize: '10px',
		color: '#64748b',
		fontFamily: 'monospace',
		backgroundColor: '#0f172a',
		padding: '1px 5px',
		borderRadius: '3px',
		display: 'inline-block'
	},
	statsGrid: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		flexShrink: 0
	},
	statItem: {
		display: 'flex',
		alignItems: 'baseline',
		gap: theme.spacing(0.5)
	},
	statLabel: {
		fontSize: '10px',
		color: '#94a3b8',
		whiteSpace: 'nowrap'
	},
	statValue: {
		fontSize: '14px',
		fontWeight: 700,
		color: '#f8fafc'
	},
	activeStatValue: {
		color: '#22c55e'
	},
	statusBadge: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1),
		fontSize: '9px',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: '10px',
		textTransform: 'uppercase',
		letterSpacing: '0.5px'
	},
	activeBadge: {
		backgroundColor: 'rgba(34, 197, 94, 0.2)',
		color: '#22c55e'
	},
	inactiveBadge: {
		backgroundColor: 'rgba(71, 85, 105, 0.2)',
		color: '#94a3b8'
	},
	cardFooter: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		backgroundColor: 'rgba(15, 23, 42, 0.5)',
		borderTop: '1px solid rgba(51, 65, 85, 0.5)',
		borderRadius: '0 0 8px 8px',
		padding: theme.spacing(1.2),
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	statsContainer: {
		display: 'flex',
		gap: theme.spacing(1.5),
		alignItems: 'center',
		flex: 1
	},
	statBadge: {
		fontSize: '9px',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: '8px',
		textTransform: 'uppercase',
		letterSpacing: '0.3px',
		display: 'flex',
		alignItems: 'center',
		gap: '4px'
	},
	totalBadge: {
		backgroundColor: 'rgba(59, 130, 246, 0.2)',
		color: '#3b82f6'
	},
	activeBadgeFooter: {
		backgroundColor: 'rgba(34, 197, 94, 0.2)',
		color: '#22c55e'
	},
	sessionCode: {
		fontSize: '10px',
		fontWeight: 600,
		color: '#64748b',
		fontFamily: 'monospace',
		backgroundColor: 'rgba(15, 23, 42, 0.5)',
		padding: '2px 6px',
		borderRadius: '4px',
		letterSpacing: '0.5px'
	}
}));

const WorkshopStatsSection = ({ workshops, themeMode = 'dark' }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);
	const history = useHistory();

	// Sort workshops: active first, then by total users (descending)
	const sortedWorkshops = workshops && workshops.length > 0 
		? [...workshops].sort((a, b) => {
			if (a.isActive && !b.isActive) return -1;
			if (!a.isActive && b.isActive) return 1;
			return b.totalUsers - a.totalUsers;
		})
		: [];

	const handleWorkshopClick = (workshop) => {
		// Navigate to workshop detail page
		history.push(`/trainings/${workshop._id}`);
	};

	return (
		<Box className={classes.root}>
			<Box className={classes.sectionTitleContainer}>
				<Box className={classes.sectionDivider} style={{ backgroundColor: colors.card.border }} />
				<Typography className={classes.sectionTitle} style={{ color: colors.text.tertiary }}>
					Workshops
				</Typography>
				<Box className={classes.sectionDivider} style={{ backgroundColor: colors.card.border }} />
			</Box>
			
			{sortedWorkshops.length === 0 ? (
				<Typography style={{ fontSize: '14px', color: colors.text.tertiary, fontStyle: 'italic' }}>
					No workshops available
				</Typography>
			) : (
				<Box 
					className={classes.scrollContainer}
					style={{
						'&::-webkit-scrollbar-track': { backgroundColor: colors.card.background },
						'&::-webkit-scrollbar-thumb': { backgroundColor: colors.background.tertiary }
					}}
				>
				{sortedWorkshops.map((workshop, index) => (
					<Paper 
						key={workshop._id || index} 
						className={classes.workshopCard} 
						elevation={0}
						onClick={() => handleWorkshopClick(workshop)}
						style={{
							backgroundColor: colors.card.background,
							border: `1px solid ${colors.card.border}`,
							'&:hover': {
								borderColor: colors.border.hover,
								boxShadow: colors.card.hoverShadow
							}
						}}
					>
						{/* Status Badge - Top Right */}
						<span 
							className={`${classes.statusBadge} ${workshop.isActive ? classes.activeBadge : classes.inactiveBadge}`}
							style={{
								backgroundColor: workshop.isActive ? colors.badge.activeBg : colors.badge.inactiveBg,
								color: workshop.isActive ? colors.badge.activeText : colors.badge.inactiveText
							}}
						>
							{workshop.isActive ? 'Active' : 'Inactive'}
						</span>

						{/* Main Content */}
						<Box className={classes.workshopHeader}>
							<Box className={`${classes.iconContainer} ${workshop.isActive ? classes.activeIcon : classes.inactiveIcon}`}>
								<SchoolIcon style={{ fontSize: '18px' }} />
							</Box>
							<Box className={classes.workshopInfo}>
								<Typography className={classes.workshopTitle} title={workshop.title} style={{ color: colors.text.primary }}>
									{workshop.title}
								</Typography>
							</Box>
						</Box>

						{/* Footer with Stats */}
						<Box 
							className={classes.cardFooter}
							style={{
								backgroundColor: colors.card.footerBg,
								borderTop: `1px solid ${colors.card.border}`
							}}
						>
							<Box className={classes.statsContainer}>
								<Box 
									className={`${classes.statBadge} ${classes.totalBadge}`}
									style={{
										backgroundColor: colors.badge.totalBg,
										color: colors.badge.totalText
									}}
								>
									<span>Total:</span>
									<span>{workshop.totalUsers}</span>
								</Box>
								<Box 
									className={`${classes.statBadge} ${classes.activeBadgeFooter}`}
									style={{
										backgroundColor: colors.badge.activeBg,
										color: colors.badge.activeText
									}}
								>
									<span>Active:</span>
									<span>{workshop.activeUsers}</span>
								</Box>
							</Box>
							{workshop.sessionCode && (
								<Box className={classes.sessionCode} style={{ color: colors.text.tertiary, backgroundColor: colors.background.primary }}>
									{workshop.sessionCode}
								</Box>
							)}
						</Box>
					</Paper>
				))}
			</Box>
			)}
		</Box>
	);
};

// Memoize to prevent unnecessary re-renders when workshops haven't changed
export default React.memo(WorkshopStatsSection, (prevProps, nextProps) => {
	return prevProps.themeMode === nextProps.themeMode &&
		JSON.stringify(prevProps.workshops) === JSON.stringify(nextProps.workshops);
});

